/**
 * Number formatting for stat tiles and tables.
 * Stat-tile values auto-compact to at most two decimals (trailing zeros
 * trimmed): 1,284 / 12.9K / 1.06M / $4.2M.
 */

export function formatCompact(value: number): string {
  if (Math.abs(value) < 10_000) {
    return value.toLocaleString("en-US");
  }
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Compact for chart bar labels and axis ticks: 3 significant digits, so
 * every label carries the same visual weight (622K, 1.06M — never 622.29K).
 * Tiles keep the two-decimal `formatCompact`; tooltips show exact values.
 */
export function formatChartLabel(value: number): string {
  if (Math.abs(value) < 10_000) {
    return value.toLocaleString("en-US");
  }
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumSignificantDigits: 3,
  }).format(value);
}

export function formatCurrencyCompact(value: number): string {
  if (Math.abs(value) < 10_000) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(value: number): string {
  return value.toLocaleString("en-US");
}

/** Exact dollars-and-cents, for amounts where cents matter ($32.50). */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

/** "Apr 6" from an ISO date, for chart axis ticks. */
export function formatMonthDay(isoDate: string): string {
  return new Date(`${isoDate.slice(0, 10)}T00:00:00`).toLocaleDateString(
    "en-US",
    { month: "short", day: "numeric" },
  );
}

/** "May 26, 2026" from an ISO date. */
export function formatDate(isoDate: string): string {
  return new Date(`${isoDate.slice(0, 10)}T00:00:00`).toLocaleDateString(
    "en-US",
    { month: "short", day: "numeric", year: "numeric" },
  );
}

/** "Aug 9, 2026, 7:12 PM" from an ISO timestamp; em dash for null. */
export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Signed delta label vs a named period, e.g. "+4.2% vs prior 30 days". */
export function formatDelta(value: number, comparison: string): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(1)}% vs ${comparison}`;
}
